// src/features/goal/contexts/ObjectiveContext.tsx
// Objective Context - 本地優先的狀態管理
//
// 設計理念：
// 1. 本地優先：所有操作立即寫入 IndexedDB，UI 即時響應
// 2. 背景同步：網路可用時自動同步到伺服器
// 3. 離線可用：無網路時仍可完整操作
// 4. 衝突解決：使用 Last-Write-Wins 策略

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  LocalObjective,
  getAllObjectives,
  createObjectiveFromRequest,
  updateObjectiveLocal,
  getActiveObjectivesCount as getActiveCountFromDB,
  getPendingObjectives,
} from '../../../services/indexedDB';
import {
  fullSync,
  pullFromServer,
  setupAutoSync,
  subscribeSyncStatus,
  processPendingObjectives,
} from '../services/objectiveSyncService';
import { CreateObjectiveRequest } from '../types/objective';
import { logger } from '../../../utils/logger';
import { taskService } from '../../task';

// Context 類型定義
interface ObjectiveContextType {
  // 狀態
  objectives: LocalObjective[];
  loading: boolean;
  error: string | null;
  isOnline: boolean;
  isSyncing: boolean;
  pendingSyncCount: number;

  // 分組數據
  activeObjectives: LocalObjective[];
  pausedObjectives: LocalObjective[];
  completedObjectives: LocalObjective[];
  activeCount: number;

  // 操作
  createObjective: (request: CreateObjectiveRequest) => Promise<LocalObjective>;
  updateObjective: (id: string, updates: Partial<LocalObjective>) => Promise<void>;
  deleteObjective: (id: string) => Promise<void>;
  pauseObjective: (id: string) => Promise<void>;
  resumeObjective: (id: string) => Promise<void>;
  completeObjective: (id: string) => Promise<void>;

  // 刷新
  refreshObjectives: () => Promise<void>;
  syncNow: () => Promise<void>;
  triggerPageViewSync: () => void; // 頁面進入時觸發的智慧同步
}

const ObjectiveContext = createContext<ObjectiveContextType | undefined>(undefined);

// Active 限制常量
const MAX_ACTIVE_OBJECTIVES = 3;

export const ObjectiveProvider: React.FC<{ children: React.ReactNode }> = ({ children }: { children: React.ReactNode }) => {
  const { currentUser: user } = useAuth();

  // 狀態
  const [objectives, setObjectives] = useState<LocalObjective[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  // Refs
  const autoSyncCleanupRef = useRef<(() => void) | null>(null);
  const initialLoadDoneRef = useRef(false);

  // 分組計算
  const { activeObjectives, pausedObjectives, completedObjectives, activeCount } = useMemo(() => {
    const active = objectives.filter(obj => obj.status === 'active');
    const paused = objectives.filter(obj => obj.status === 'paused');
    const completed = objectives.filter(obj => obj.status === 'completed');

    return {
      activeObjectives: active,
      pausedObjectives: paused,
      completedObjectives: completed,
      activeCount: active.length,
    };
  }, [objectives]);

  // 從本地載入 Objectives
  const loadFromLocal = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const localObjectives = await getAllObjectives(user.uid, { excludeArchived: true });
      setObjectives(localObjectives);
      setError(null);
    } catch (err) {
      logger.error('ObjectiveContext: Failed to load from local', err);
      setError('Failed to load objectives');
    }
  }, [user?.uid]);

  // 初始載入
  useEffect(() => {
    if (!user?.uid) {
      setObjectives([]);
      setLoading(false);
      return;
    }

    const initialize = async () => {
      setLoading(true);

      try {
        // 1. 先從本地載入（立即顯示）
        await loadFromLocal();

        // 2. 如果在線，從伺服器拉取最新資料
        if (navigator.onLine && !initialLoadDoneRef.current) {
          try {
            await pullFromServer(user.uid);
            await loadFromLocal(); // 重新載入本地資料
          } catch (err) {
            // 拉取失敗不影響本地資料顯示
            logger.warn('ObjectiveContext: Failed to pull from server', err);
          }
        }

        initialLoadDoneRef.current = true;
      } catch (err) {
        logger.error('ObjectiveContext: Initialize failed', err);
        setError('Failed to initialize objectives');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [user?.uid, loadFromLocal]);

  // 監聽網路狀態
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      logger.dev('ObjectiveContext: Online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      logger.dev('ObjectiveContext: Offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 設置自動同步
  useEffect(() => {
    if (!user?.uid) return;

    // 訂閱同步狀態
    const unsubscribeSyncStatus = subscribeSyncStatus((syncing, pending) => {
      setIsSyncing(syncing);
      setPendingSyncCount(pending);
    });

    // 設置自動同步（網路恢復時觸發）
    autoSyncCleanupRef.current = setupAutoSync(user.uid);

    return () => {
      unsubscribeSyncStatus();
      if (autoSyncCleanupRef.current) {
        autoSyncCleanupRef.current();
      }
    };
  }, [user?.uid]);

  // 同步完成後重新載入本地資料
  useEffect(() => {
    if (!isSyncing && pendingSyncCount === 0 && initialLoadDoneRef.current) {
      loadFromLocal();
    }
  }, [isSyncing, pendingSyncCount, loadFromLocal]);

  // 創建 Objective
  const createObjective = useCallback(async (request: CreateObjectiveRequest): Promise<LocalObjective> => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    // 檢查 Active 數量限制
    if (request.status !== 'paused') {
      const currentActiveCount = await getActiveCountFromDB(user.uid);
      if (currentActiveCount >= MAX_ACTIVE_OBJECTIVES) {
        throw new Error(`You can only have ${MAX_ACTIVE_OBJECTIVES} active goals at a time`);
      }
    }

    try {
      // 本地創建（立即生效）
      const newObjective = await createObjectiveFromRequest(user.uid, request);

      // 更新狀態（樂觀更新）
      setObjectives(prev => [newObjective, ...prev]);

      // 背景同步
      if (navigator.onLine) {
        processPendingObjectives(user.uid).catch(err => {
          logger.warn('ObjectiveContext: Background sync failed', err);
        });
      }

      return newObjective;
    } catch (err) {
      logger.error('ObjectiveContext: Failed to create objective', err);
      throw err;
    }
  }, [user?.uid]);

  // 更新 Objective
  const updateObjective = useCallback(async (id: string, updates: Partial<LocalObjective>): Promise<void> => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      // 樂觀更新 UI（立即響應）
      setObjectives(prev => prev.map(obj =>
        obj.id === id
          ? { ...obj, ...updates, updatedAt: Date.now() }
          : obj
      ));

      // 本地持久化（寫入 IndexedDB）
      await updateObjectiveLocal(id, updates);

      // 背景同步
      if (navigator.onLine) {
        processPendingObjectives(user.uid).catch(err => {
          logger.warn('ObjectiveContext: Background sync failed', err);
        });
      }
    } catch (err) {
      logger.error('ObjectiveContext: Failed to update objective', err);
      throw err;
    }
  }, [user?.uid]);

  // 刪除 Objective（軟刪除 - 改為 archived）
  const deleteObjective = useCallback(async (id: string): Promise<void> => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    try {
      // 樂觀更新 UI（立即從列表移除）
      setObjectives(prev => prev.filter(obj => obj.id !== id));

      // 本地持久化（軟刪除：更新狀態為 archived，標記為 pending 同步）
      await updateObjectiveLocal(id, { status: 'archived' });

      // 背景同步
      if (navigator.onLine) {
        processPendingObjectives(user.uid).catch(err => {
          logger.warn('ObjectiveContext: Background sync failed', err);
        });
      }
    } catch (err) {
      logger.error('ObjectiveContext: Failed to delete objective', err);
      throw err;
    }
  }, [user?.uid]);

  // 暫停 Objective
  const pauseObjective = useCallback(async (id: string): Promise<void> => {
    await updateObjective(id, { status: 'paused' });
  }, [updateObjective]);

  // 恢復 Objective
  const resumeObjective = useCallback(async (id: string): Promise<void> => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }

    // 檢查 Active 數量限制
    const currentActiveCount = await getActiveCountFromDB(user.uid);
    if (currentActiveCount >= MAX_ACTIVE_OBJECTIVES) {
      throw new Error(`You can only have ${MAX_ACTIVE_OBJECTIVES} active goals at a time`);
    }

    await updateObjective(id, { status: 'active' });
  }, [user?.uid, updateObjective]);

  // 完成 Objective
  const completeObjective = useCallback(async (id: string): Promise<void> => {
    const now = Date.now();
    await updateObjective(id, {
      status: 'completed',
      completedAt: now,
    });

    // 背景 drop 所有關聯的 OPEN tasks（fire-and-forget，不阻塞 Goal 完成流程）
    if (user?.uid) {
      taskService.dropTasksByObjectiveId(id, user.uid).catch(err => {
        logger.warn('ObjectiveContext: Failed to drop tasks on goal completion', err);
      });
    }
  }, [user?.uid, updateObjective]);

  // 手動刷新
  const refreshObjectives = useCallback(async (): Promise<void> => {
    if (!user?.uid) return;

    setLoading(true);

    try {
      // 如果在線，從伺服器拉取
      if (navigator.onLine) {
        await pullFromServer(user.uid);
      }

      // 重新載入本地資料
      await loadFromLocal();
    } catch (err) {
      logger.error('ObjectiveContext: Failed to refresh', err);
      setError('Failed to refresh objectives');
    } finally {
      setLoading(false);
    }
  }, [user?.uid, loadFromLocal]);

  // 手動同步
  const syncNow = useCallback(async (): Promise<void> => {
    if (!user?.uid || !navigator.onLine) return;

    try {
      await fullSync(user.uid);
      await loadFromLocal();
    } catch (err) {
      logger.error('ObjectiveContext: Sync failed', err);
    }
  }, [user?.uid, loadFromLocal]);

  // 頁面進入時觸發的智慧同步（無冷卻限制）
  const triggerPageViewSync = useCallback(() => {
    if (!user?.uid || !navigator.onLine) {
      logger.dev('ObjectiveContext: Skip page view sync (offline or no user)');
      return;
    }

    logger.info('ObjectiveContext: Triggering page view sync');

    // 背景執行智慧同步（不阻塞 UI）
    (async () => {
      try {
        // 記錄同步前的狀態
        const beforeSync = await getAllObjectives(user.uid, { excludeArchived: true });
        logger.dev('ObjectiveContext: Before sync', {
          totalObjectives: beforeSync.length,
          activeCount: beforeSync.filter(o => o.status === 'active').length,
        });

        // 檢查是否有待同步項目
        const pending = await getPendingObjectives(user.uid);

        if (pending.length > 0) {
          // 有本地變更：執行完整雙向同步
          logger.info('ObjectiveContext: Has pending changes, full sync', {
            pendingCount: pending.length
          });
          await fullSync(user.uid);
        } else {
          // 無本地變更：只拉取伺服器資料
          logger.info('ObjectiveContext: No pending changes, pull only');
          await pullFromServer(user.uid);
        }

        // 重新載入本地資料以更新 UI
        logger.dev('ObjectiveContext: Reloading local data after sync');
        await loadFromLocal();

        // 記錄同步後的狀態
        const afterSync = await getAllObjectives(user.uid, { excludeArchived: true });
        logger.info('ObjectiveContext: After sync', {
          totalObjectives: afterSync.length,
          activeCount: afterSync.filter(o => o.status === 'active').length,
        });
      } catch (err) {
        // 同步失敗不影響本地操作
        logger.warn('ObjectiveContext: Page view sync failed', err);
      }
    })();
  }, [user?.uid, loadFromLocal]);

  const value: ObjectiveContextType = {
    objectives,
    loading,
    error,
    isOnline,
    isSyncing,
    pendingSyncCount,
    activeObjectives,
    pausedObjectives,
    completedObjectives,
    activeCount,
    createObjective,
    updateObjective,
    deleteObjective,
    pauseObjective,
    resumeObjective,
    completeObjective,
    refreshObjectives,
    syncNow,
    triggerPageViewSync,
  };

  return (
    <ObjectiveContext.Provider value={value}>
      {children}
    </ObjectiveContext.Provider>
  );
};

// Hook
export const useObjectives = (): ObjectiveContextType => {
  const context = useContext(ObjectiveContext);
  if (context === undefined) {
    throw new Error('useObjectives must be used within an ObjectiveProvider');
  }
  return context;
};

export default ObjectiveContext;
